import { Avatar, Box, Grid, styled, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useContext, useEffect, useMemo } from "react";
import { AmbassadorProfileContext } from "..";
import { LabelWithIcon } from "../../../../components/form";
import { EditButton } from "../../../../components/form/buttons";
import FlexBox from "../../../../components/layouts/Flexbox";
import ModalBox from "../../../../components/modal";
import { IMAGES_PATH } from "../../../../constants";
import { APP_COLORS, ENUM_USER_ROLE } from "../../../../enums";
import useToggle from "../../../../hooks/useToggle";
import {
  convertToCompanyAndTitleName,
  convertToFullName,
} from "../../../../services/utils";
import { useAppSelector } from "../../../../store";
import { getNoOfEmpLabel } from "../../../my-profile/vendor-profile/utils/dataTransformers";
import EditMyBasicDetails from "./edit";
import { Buildings, GlobeHemisphereEast, UsersThree } from "@phosphor-icons/react";

const CoverContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  height: 138,
  background: `url(${IMAGES_PATH}/ambassdor-cover-img.jpg)`,
}));

const EditButtonContainer = styled(Box)({
  position: "absolute",
  top: 16,
  right: 16,
  zIndex: 2,
});

const ProfilePictureContainer = styled(Box)({
  position: "absolute",
  bottom: -60,
  left: 24,
  zIndex: 1,
});

const ProfilePictureWrapper = styled(Box)({
  position: "relative",
});

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  [theme.breakpoints.up("sm")]: {
    width: 140,
    height: 140,
  },
  border: `4px solid ${APP_COLORS.white}`,
  backgroundColor: theme.palette.secondary.main,
  fontSize: 48,
  [theme.breakpoints.up("sm")]: {
    fontSize: 56,
  },
}));

function BasicDetails() {
  const { data, editMode, showCompanyName } =
    useContext(AmbassadorProfileContext);
  const [openEditModal, setOpenEditModal] = useToggle(false);
  const userData = useAppSelector((state) => state.auth.user);
  const profileData = useAppSelector((state) => state.profile.data);
  const lists = useAppSelector((state) => state.lists);

  const noOfEmpsList = useMemo(
    () => lists?.no_of_emps?.data || [],
    [lists?.no_of_emps?.data]
  );
  const industriesList = useMemo(
    () => lists?.industries?.data || [],
    [lists?.industries?.data]
  );

  const employeeCountLabel = useMemo(() => {
    if (!data?.no_of_emp) return null;
    const label = getNoOfEmpLabel(noOfEmpsList, data.no_of_emp);
    return label || data.no_of_emp;
  }, [data?.no_of_emp, noOfEmpsList]);

  const industryLabel = useMemo(() => {
    if (!data?.industry) return null;
    const option = industriesList.find(
      (item: any) => item?.id === data.industry || item?.label === data.industry
    );
    return option?.label ?? data.industry;
  }, [data?.industry, industriesList]);

  useEffect(() => {
    if (editMode && data && !data?.industry) {
      setOpenEditModal();
    }
  }, [editMode, data?.id, data?.industry]);

  const handleOnSubmit = () => {
    setOpenEditModal(false);
  };

  const onEditCancel = () => {
    setOpenEditModal(false);
  };

  const getAvatarSrc = () => {
    if (!data) return null;
    return userData?.profileId === data?.id || showCompanyName
      ? data?.photo || data?.picture
      : null;
  };

  const getAvatarInitial = () => {
    if (!data) return "";
    return userData?.profileId === data?.id || showCompanyName
      ? data?.firstName?.[0] || ""
      : data?.title?.[0] || "";
  };

  const renderIcon = ({ icon }: { icon: React.ReactNode }) => (
    <FlexBox
      alignItems="center"
      justifyContent="center"
      minHeight={44}
      minWidth={44}
      height={44}
      width={44}
      bgcolor={APP_COLORS.primaryLight}
      borderRadius={3}
    >
      {icon}
    </FlexBox>
  );

  const renderInfoCard = (
    icon: React.ReactNode,
    label: string,
    value: string | null | undefined,
    labelVariant: "small" | "small-ibmPlexMono" = "small-ibmPlexMono",
    withBorderRadius = false
  ) => {
    if (!value) return null;

    const content = (
      <FlexBox gap={1.5}>
        {renderIcon({ icon })}
        <Stack gap={0.25}>
          <Typography variant={labelVariant} color={APP_COLORS.grey}>
            {label}
          </Typography>
          <Typography
            variant="body-ibmPlexMono-medium"
            color={APP_COLORS.textDark}
          >
            {value}
          </Typography>
        </Stack>
      </FlexBox>
    );

    return withBorderRadius ? (
      <FlexBox borderRadius={3}>{content}</FlexBox>
    ) : (
      content
    );
  };

  return (
    <>
      <Stack gap={1} borderBottom={1} borderColor={APP_COLORS.greyLight3}>
        <Box height={200}>
          <CoverContainer>
            {editMode && (
              <EditButtonContainer>
                <EditButton size="medium" onClick={setOpenEditModal} text="Edit Profile" />
              </EditButtonContainer>
            )}

            <ProfilePictureContainer>
              <ProfilePictureWrapper>
                <ProfileAvatar src={getAvatarSrc()}>
                  {getAvatarInitial()}
                </ProfileAvatar>
              </ProfilePictureWrapper>
            </ProfilePictureContainer>
          </CoverContainer>
        </Box>

        <Stack gap={0.5}>
          <Typography variant="bodyLarge-mono-medium">
            {ENUM_USER_ROLE.AMBASSADOR === userData?.role || showCompanyName
              ? convertToFullName(data)
              : convertToCompanyAndTitleName(data)}
          </Typography>

          {data?.title && (
            <Typography
              variant="body-ibmPlexMono"
              color={APP_COLORS.textSecondary}
            >
              {data?.title}
            </Typography>
          )}
          <Stack gap={2} py={2}>
            {showCompanyName !== false &&
              (userData?.role === ENUM_USER_ROLE.AMBASSADOR
                ? data?.company
                : profileData?.licenseForMeeting > 0 && data?.company) &&
              renderInfoCard(<Buildings size={24} />, "Company", data?.company)}

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                {renderInfoCard(
                  <Buildings size={24} />,
                  "Industry",
                  industryLabel,
                  "small",
                  true
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {renderInfoCard(
                  <UsersThree size={24} />,
                  "Employees",
                  employeeCountLabel,
                  "small-ibmPlexMono",
                  true
                )}
              </Grid>
            </Grid>

            {data?.city && (
              <LabelWithIcon label={data?.city} icon={<GlobeHemisphereEast size={20} />} />
            )}

            <Typography
              variant="body-ibmPlexMono"
              title={data?.description}
              color={APP_COLORS.textMedium}
            >
              {data?.description}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {openEditModal && (
        <ModalBox
          title={"Edit your current environment"}
          onClose={onEditCancel}
          showFooter={false}
        >
          {data && (
            <EditMyBasicDetails
              onCancel={onEditCancel}
              data={data}
              onSubmit={handleOnSubmit}
            />
          )}
        </ModalBox>
      )}
    </>
  );
}

export default BasicDetails;
